import { useCallback, useEffect, useState } from "react";
import { Globe, Loader2, Inbox, PencilLine } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WebProfileEditor from "./WebProfileEditor";

const Leads = ({ items }) => (
  <div className="mt-4" data-testid="wp-leads">
    <div className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-muted-foreground"><Inbox className="h-4 w-4" />Lead dal sito ({items.length})</div>
    {!items.length ? <p className="text-xs text-muted-foreground" data-testid="wp-leads-empty">Nessun contatto ricevuto da torri.solar.</p> : (
      <div className="max-h-72 divide-y divide-border overflow-y-auto rounded-lg border border-border">
        {items.map((l) => (
          <div key={l.id} className="p-3 text-sm" data-testid={`wp-lead-${l.id}`}>
            <div className="flex flex-wrap justify-between gap-2"><b>{l.nome || l.email}</b><span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString("it-IT")}</span></div>
            <div className="text-xs text-muted-foreground">{[l.email, l.telefono, l.comune, l.servizio].filter(Boolean).join(" · ")}</div>
            {l.messaggio && <p className="mt-1 whitespace-pre-line text-xs">{l.messaggio}</p>}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default function WebProfileCard({ partnerId }) {
  const [d, setD] = useState(null);
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/partners/${partnerId}/web-profile`).then((r) => setD(r.data)).catch(() => {});
    api.get(`/partners/${partnerId}/web-leads`).then((r) => setLeads(r.data.items)).catch(() => {});
  }, [partnerId]);
  useEffect(load, [load]);
  const toggle = async (v) => {
    setBusy(true);
    try { const r = await api.put(`/partners/${partnerId}/web-profile/attivo`, { attivo: v }); setD(r.data); toast.success(v ? "Web Profile attivato: il partner lo vede in My Vesta" : "Web Profile disattivato"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setBusy(false);
  };
  if (!d) return null;
  const pub = d.attivo && d.profile.page_enabled;
  return (
    <Card className="p-6" data-testid="web-profile-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold tracking-tight"><Globe className="h-5 w-5 text-[#C17F59]" />Web Profile</h2>
          <p className="text-xs text-muted-foreground">Pagina del partner su torri.solar{d.attivo ? ` · /${d.profile.slug}` : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          {d.attivo && <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${pub ? "bg-green-500/15 text-green-500" : "bg-amber-500/15 text-amber-500"}`} data-testid="wp-status">{pub ? "Online" : d.issues.length ? `Da completare (${d.issues.length})` : "Non pubblicata"}</span>}
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          <Switch checked={d.attivo} onCheckedChange={toggle} disabled={busy} data-testid="wp-attivo-switch" />
        </div>
      </div>
      {d.attivo && (
        <>
          <button type="button" onClick={() => setOpen(true)} data-testid="wp-open-editor"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C17F59] px-4 py-2 text-sm font-bold text-black transition-opacity hover:opacity-90"><PencilLine className="h-4 w-4" />Modifica Web Profile</button>
          <Leads items={leads} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-neutral-800 bg-neutral-950" data-testid="wp-dialog" aria-describedby={undefined}>
              <DialogHeader><DialogTitle className="text-white">Web Profile</DialogTitle></DialogHeader>
              <WebProfileEditor base={`/partners/${partnerId}`} data={d} onSaved={setD} />
            </DialogContent>
          </Dialog>
        </>
      )}
    </Card>
  );
}
